// SINGLE SOURCE OF TRUTH for every image in the app (rules.md #8).
// Components reference assets ONLY by ID via <AssetImage id="..."/> or getAsset(id).
// IDs follow the delivered art sheets (lowercase card ids, chip_1000 not chip_1k, etc.).
// Files live flat at /assets/<id>.png — processed by scripts/import_cleaned.py, served by scripts/publish_assets.py.

export interface AssetMeta {
  path: string;
  width: number;
  height: number;
  aspectRatio: number;
  alt: string;
}

const BASE = "/assets";
const asset = (id: string, width: number, height: number, alt: string): AssetMeta => ({
  path: `${BASE}/${id}.png`,
  width,
  height,
  aspectRatio: width / height,
  alt,
});
const square = (id: string, alt: string) => asset(id, 512, 512, alt);
const plate = (id: string, alt: string) => asset(id, 1024, 687, alt);
const scene = (id: string, alt: string) => asset(id, 1264, 848, alt);
const tableArt = (id: string, alt: string) => asset(id, 1200, 600, alt);

export const CARD_RANKS = ["2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K", "A"] as const;
export const CARD_SUITS = ["s", "h", "d", "c"] as const;
export const CARD_W = 500;
export const CARD_H = 745;

const SUIT_NAME: Record<string, string> = { s: "spades", h: "hearts", d: "diamonds", c: "clubs" };
const RANK_NAME: Record<string, string> = { T: "Ten", J: "Jack", Q: "Queen", K: "King", A: "Ace" };

const cards: Record<string, AssetMeta> = {};
for (const r of CARD_RANKS) {
  for (const s of CARD_SUITS) {
    const id = `card_face_${r}${s}`.toLowerCase();
    cards[id] = asset(id, CARD_W, CARD_H, `${RANK_NAME[r] ?? r} of ${SUIT_NAME[s]}`);
  }
}

export const DEALER_IDS = ["dealer_female_01", "dealer_male_01", "dealer_female_02", "dealer_male_02"] as const;
const dealers = Object.fromEntries(DEALER_IDS.map((id) => [id, square(id, `Dealer ${id.replace("dealer_", "").replace("_", " ")}`)]));

export const CHIP_DENOMS = [
  { id: "chip_1", value: 1 }, { id: "chip_5", value: 5 }, { id: "chip_25", value: 25 }, { id: "chip_100", value: 100 },
  { id: "chip_500", value: 500 }, { id: "chip_1000", value: 1000 }, { id: "chip_5k", value: 5000 }, { id: "chip_10k", value: 10000 },
  { id: "chip_25k", value: 25000 }, { id: "chip_50k", value: 50000 }, { id: "chip_100k", value: 100000 },
  { id: "chip_250k", value: 250000 }, { id: "chip_1m", value: 1000000 },
] as const;
const chips = Object.fromEntries(CHIP_DENOMS.map((c) => [c.id, square(c.id, `${c.value.toLocaleString()} chip`)]));

export const FELT_IDS = ["table_felt_green", "table_felt_blue", "table_felt_red", "table_felt_black"] as const;
const felts = Object.fromEntries(FELT_IDS.map((id) => [id, tableArt(id, `${id.split("_")[2]} table`)]));

export const BG_IDS = ["bg_lobby_main", "bg_lobby_dark", "bg_lobby_green", "bg_table", "bg_landing", "bg_splash", "bg_splash_banner"] as const;
const BG_ALT: Record<string, string> = { bg_table: "Card room", bg_landing: "Players at the Players Room table", bg_splash: "Players Room — the fairest poker room", bg_splash_banner: "Players Room — the fairest poker room" };
const backgrounds = Object.fromEntries(BG_IDS.map((id) => [id, scene(id, BG_ALT[id] ?? "Lobby background")]));

export const ACTION_BUTTON_IDS = ["btn_action_fold", "btn_action_check", "btn_action_call", "btn_action_bet", "btn_action_raise", "btn_action_allin", "btn_action_checkfold", "btn_action_callany"] as const;
const actionButtons = Object.fromEntries(ACTION_BUTTON_IDS.map((id) => [id, asset(id, 600, 260, id.replace("btn_action_", "").replace("allin", "all in").replace("checkfold", "check / fold").replace("callany", "call any"))]));

export const ASSETS: Record<string, AssetMeta> = {
  ...cards,
  card_back_default: asset("card_back_default", CARD_W, CARD_H, "Card back"),
  ...dealers,
  ...chips,
  ...felts,
  ...backgrounds,
  ...actionButtons,
  "table_felt_qbp-v2": tableArt("table_felt_qbp-v2", "Queen B's table"),  // club felt, not in the player felt picker
  tex_carpet: asset("tex_carpet", 1024, 1024, "Art-deco carpet"),
  logo_emblem: square("logo_emblem", "Players Room emblem"),
  logo_wordmark: plate("logo_wordmark", "Players Room"),
  dealer_button: square("dealer_button", "Dealer button"),
  btn_sit: square("btn_sit", "Sit here"),
  seat_glow_ring: square("seat_glow_ring", "Glowing gold ring — open Sit N Go seat"),
  sng_wait_coin: square("sng_wait_coin", "Players Room gold coin — waiting for players"),
  button_sb: square("button_sb", "Small blind"),
  button_bb: square("button_bb", "Big blind"),
  pot_display: plate("pot_display", "Pot"),
  player_avatar_frame: square("player_avatar_frame", "Avatar frame"),
  player_nameplate: plate("player_nameplate", "Name plate"),
  avatar_dragon: square("avatar_dragon", "Dragon avatar"),
  avatar_lion: square("avatar_lion", "Lion avatar"),
};

export type AssetId = string; // keys of ASSETS; validated at runtime by getAsset

// Two chip sets, both lettered PLAYER'S ROOM: cash chips carry $ values, tournament (MTT / Sit & Go) chips plain numbers.
// The table page picks the set; everywhere else is cash. Bump -vN when the art changes (nginx caches /assets 30 days).
const TOUR_CHIPS = new Set(["chip_100", "chip_500", "chip_1000", "chip_5k", "chip_10k", "chip_25k", "chip_50k"]);
let CHIP_SET: "cash" | "tour" = "cash";
export const setChipSet = (set: "cash" | "tour") => { CHIP_SET = set; };
const chipPath = (id: string) => `${BASE}/chips/${CHIP_SET === "tour" && TOUR_CHIPS.has(id) ? "tour" : "cash"}/${id}-v1.png`;

export function getAsset(id: AssetId): AssetMeta {
  const a = ASSETS[id];
  if (!a) throw new Error(`Unknown asset id "${id}" — add it to assetManifest.ts first`);
  return id in chips ? { ...a, path: chipPath(id) } : a;
}

// engine cards are e.g. "As", "Td"; manifest ids are lowercase
export const cardAssetId = (card: string): AssetId => (card && card !== "XX" ? `card_face_${card.toLowerCase()}` : "card_back_default");

export function chipAssetId(amount: number): AssetId {
  let best: AssetId = "chip_1";
  for (const c of CHIP_DENOMS) if (c.value <= amount) best = c.id;
  return best;
}
